import { LexicalEditor } from 'lexical'
import React, { useState, FC, useEffect, useMemo } from 'react'
import { Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { ValidationError } from 'yup'
import { INSERT_TABLE_COMMAND } from '@lexical/table'

import { FormField } from './FormField'
import { getInsertTableSchema, FormValidationError } from './validation'
import { useCustomTranslation } from '../../../../Hooks/useCustomTranslation'
import { Dialog } from '../../../Dialog'

interface InsertTableDialogProps {
  activeEditor: LexicalEditor
  open: boolean
  onClose: () => void
}

export interface FormData {
  columns: string
  rows: string
}

export type ValidationErrorKey = keyof FormData | null

const useStyles = makeStyles()(theme => ({
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: `${theme.spacing(4)} 0 !important`,
    gap: theme.spacing(2),
  },
}))

const initialFormData = {
  columns: '',
  rows: '',
}

/**
 * Dialog for adding new table in the markdown editor
 */
export const InsertTableDialog: FC<InsertTableDialogProps> = ({ activeEditor, open, onClose }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const { columns, rows } = formData
  const [validationError, setValidationError] = useState<FormValidationError>(null)

  const insertTableSchema = useMemo(() => getInsertTableSchema(t), [t])

  const getValidationErrors = async (): Promise<FormValidationError> => {
    let error: FormValidationError = null

    try {
      await insertTableSchema.validate(formData)
    } catch (err) {
      error = err as ValidationError
    }

    return error
  }

  const onClick = async (): Promise<void> => {
    const error = await getValidationErrors()

    if (error) {
      setValidationError(error)
      return
    } else {
      setValidationError(null)
    }

    activeEditor.dispatchCommand(INSERT_TABLE_COMMAND, {
      columns,
      rows,
    })

    onClose()
  }

  const onChangeFormData = (value: string, key: keyof FormData): void => {
    setFormData(oldData => ({
      ...oldData,
      [key]: value,
    }))
  }

  useEffect(() => {
    setFormData(initialFormData)
  }, [open])

  return (
    <Dialog
      onClose={onClose}
      maxWidth="sm"
      title={t('shell.markdown-editor.insert-table')}
      actions={{
        primary: {
          name: t('shell.markdown-editor.insert'),
          onClick: onClick,
        },
        secondary: {
          name: t('shell.markdown-editor.cancel'),
        },
      }}
    >
      <Box className={classes.container}>
        <FormField
          label={t('shell.markdown-editor.columns')}
          placeholder={t('shell.markdown-editor.columns')}
          onChange={onChangeFormData}
          value={columns}
          error={validationError}
          fieldKey={'columns'}
        />
        <FormField
          label={t('shell.markdown-editor.rows')}
          placeholder={t('shell.markdown-editor.rows')}
          onChange={onChangeFormData}
          value={rows}
          error={validationError}
          fieldKey={'rows'}
        />
      </Box>
    </Dialog>
  )
}
